<script>
import { displayToast, handleResponseError } from '@/components/utils/globalFunction'
import RepairFileProspectService from '@/services/repairModule/repairFile/RepairFileProspectService'
import Validator from '@/components/utils/validator'

export default {
  name: 'RepairFileProspectManufacturerOptionSelectedPage',
  data() {
    return {
      loading: false,
      tokenValidity: false,

      logoPath: null,
      networkPrimaryColor: null,

      token: '',
      manufacturer_choice: null,

      formSubmissionToken: null,
      prospectSupportNumber: null,
      prospectComment: null,

      file: null,

      max12CharRules: Validator.max12CharRules,
      max150CharRules: Validator.max150CharRules,

      formValid: false,
      hideForm: false,

      error: null
    }
  },
  async created() {
    this.loading = true

    await this.$store.dispatch('auth/cleanSession')
    const urlParams = new URLSearchParams(window.location.search)

    this.token = urlParams.get('token')
    this.manufacturer_choice = urlParams.get('option')
    try {
      const data = {
        email_token: this.token,
        choice_selected: this.manufacturer_choice
      }
      const response =
        await RepairFileProspectService.saveManufacturerProspectOptionChoiceFromEmailSelection(data)
      this.logoPath = response.logo_path
      this.networkPrimaryColor = response.network_primary_color
      this.formSubmissionToken = response.manufacturer_information_token
      this.tokenValidity = true
    } catch (error) {
      handleResponseError(error)
    } finally {
      this.loading = false
    }
  },
  methods: {
    async sendProspectManufacturerResponseInformation() {
      this.loading = true

      try {
        let data = {
          prospect_support_number: this.prospectSupportNumber,
          prospect_manufacturer_comment: this.prospectComment,
          token: this.formSubmissionToken
        }

        if (this.file) {
          const formData = new FormData()
          formData.append('data', JSON.stringify(data))
          formData.append('file', this.file)
          await RepairFileProspectService.sendProspectManufacturerResponseInformation(formData)
        } else {
          await RepairFileProspectService.sendProspectManufacturerResponseInformation(data)
        }

        displayToast(
          this.$t('PUBLIC.OPTION.SUCCESS_EMAIL_SEND_WITH_ATTACHMENT', {
            supportAddress: 'support@boomerang-exploration.com'
          }),
          'success'
        )
        this.hideForm = true
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<template>
  <v-app>
    <v-main class="main">
      <div v-if="tokenValidity && !hideForm" class="container">
        <div class="picture-content">
          <v-img :src="logoPath" />
        </div>
        <div class="title">
          {{ $t('PUBLIC_PROSPECT.TITLE') }}
        </div>

        <div class="message-container">
          <div class="message">
            {{ $t('PUBLIC_PROSPECT.MESSAGE') }}
          </div>
        </div>

        <div class="message-container">
          <div class="manufacturer-info-message">
            {{ $t('PUBLIC_PROSPECT.MANUFACTURER_INFO_MESSAGE') }}
          </div>
        </div>

        <v-container class="form-container">
          <v-form
            @submit.prevent="sendProspectManufacturerResponseInformation()"
            v-model="formValid"
          >
            <!--Order number-->
            <v-row>
              <v-col col="6">
                <p class="mb-4">
                  {{ $t('PUBLIC_PROSPECT.SUPPORT_NUMBER_LABEL') }}
                </p>
                <v-text-field
                  v-model="prospectSupportNumber"
                  :error-messages="error?.prospectSupportNumber"
                  :hide-details="!error?.prospectSupportNumber"
                  :rules="max12CharRules"
                  :counter="12"
                  :maxlength="12"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col col="6">
                <p class="mb-4">
                  {{ $t('PUBLIC_PROSPECT.MANUFACTURER_COMMENT_LABEL') }}
                </p>
                <v-textarea
                  v-model.trim="prospectComment"
                  :placeholder="$t('PUBLIC_PROSPECT.MANUFACTURER_COMMENT_LABEL')"
                  auto-grow
                  rows="1"
                  :error-messages="error?.prospectComment"
                  :hide-details="!error?.prospectComment"
                  :rules="max150CharRules"
                  :maxlength="150"
                  :counter="150"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col col="6">
                <!--Add attachment-->
                <p class="mb-4">
                  {{ $t('PUBLIC_PROSPECT.MANUFACTURER_ATTACHMENT_LABEL') }}
                </p>
                <v-file-input
                  class="logo-selector"
                  chips
                  counter
                  show-size
                  accept=".pdf, .jpg, .png"
                  v-model="file"
                  :hide-details="!error?.file"
                >
                  <template v-slot:append-inner>
                    <img
                      alt="download.svg"
                      class="download-svg mr-4"
                      src="@/assets/icons/file/download.svg"
                    />
                  </template>
                </v-file-input>
              </v-col>
            </v-row>

            <div class="btn-container mt-4">
              <v-btn
                class="boom-button"
                :disabled="loading"
                :loading="loading"
                variant="outlined"
                type="submit"
              >
                {{ $t('COMMON.BUTTON.SEND') }}
              </v-btn>
            </div>
          </v-form>
        </v-container>
      </div>
      <div v-if="tokenValidity && hideForm" class="container">
        <div class="picture-content">
          <v-img :src="logoPath" />
        </div>
        <div class="title">
          {{ $t('PUBLIC_PROSPECT.TITLE_FORM_SENT') }}
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: center;
  align-items: stretch;
}

.title {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  font-size: x-large;
  font-family: 'Roboto', serif;
  align-content: center;
}

.message-container {
  display: flex;
  flex-direction: row;
  font-size: x-large;
  font-family: 'Roboto', serif;
  justify-content: center;
  flex-wrap: nowrap;
  align-content: center;
  align-items: flex-start;
}

.btn-container {
  display: flex;
  flex-direction: row;
  font-size: x-large;
  font-weight: bold;
  font-family: 'Roboto', serif;
  justify-content: center;
  flex-wrap: nowrap;
  align-content: center;
  align-items: flex-start;
}

.form-container {
  font-family: 'Roboto', serif;
  max-width: 800px;
}

.message {
  display: flex;
  flex-direction: row;
  font-size: x-large;
  font-family: 'Roboto', serif;
  justify-content: center;
  flex-wrap: nowrap;
  align-content: center;
  align-items: center;
}

.manufacturer-info-message {
  display: flex;
  flex-direction: row;
  font-size: x-large;
  font-family: 'Roboto', serif;
  justify-content: center;
  flex-wrap: nowrap;
  align-content: center;
  align-items: center;
  margin-top: 12px;
}

.picture-content {
  margin: 5vh auto auto;
  width: 100%;
  height: 100%;
  max-width: 250px;
}

:deep(.logo-selector .v-field.v-field--appended) {
  justify-content: center !important;
  align-content: center;
}

.download-svg {
  height: 20px;
  filter: invert(98%) sepia(0%) saturate(1853%) hue-rotate(181deg) brightness(96%) contrast(80%);
}
</style>
